/**
 * coachDefer.ts — never start (or keep showing) a tour card on top of a
 * blocking dialog or an open menu.
 *
 * TemplateModal and friends render via ui/Modal, which sets
 * `aria-modal="true"`, so the post-onboarding `/templates` landing (and any
 * future full-screen modal) is recognised with no per-caller special-casing.
 * The waiting itself lives in lib/coachWait.ts (`waitUntil`), because an
 * automatic start also has to wait for its anchor to mount — one poller,
 * one predicate that combines both.
 */

import { anchorElement } from './coachDom';

// Real modals (ui/Modal sets aria-modal="true") AND open dropdown menus:
// the account popover, the kebab menus, the mode/viewport pickers all render
// role="menu" only while open, and a tour card sitting on top of a menu the
// person just opened hides the very thing they opened it for.
export const BLOCKING_DIALOG = '[role="dialog"][aria-modal="true"], [role="menu"]';

export function isBlockingDialogOpen(doc: Document = document): boolean {
  try {
    return !!doc.querySelector(BLOCKING_DIALOG);
  } catch {
    return false;
  }
}

/** The open blocking dialog, if any — so a card whose anchor lives INSIDE it
 *  (a button in a modal) can stay visible while everything outside pauses. */
export function blockingDialogElement(doc: Document = document): Element | null {
  try {
    return doc.querySelector(BLOCKING_DIALOG);
  } catch {
    return null;
  }
}

/** True when a blocking dialog is open AND the anchor is not inside it — i.e.
 *  the dialog really covers the target. A nudge whose own anchor lives inside
 *  the open dialog (the "Use this template" button in the picked-template
 *  modal) is not blocked by it — see showNudge's `ready()`. */
export function anchorBlockedByDialog(anchor: string, doc: Document = document): boolean {
  const dlg = blockingDialogElement(doc);
  if (!dlg) return false;
  const el = anchorElement(anchor);
  return !(el && dlg.contains(el));
}
